import { extractLastNameComponent } from '@spotify/confidence-core-react';
import {
  ActivityChangeOptions,
  ActivityItem,
  ActivityLabelItem,
  ActivityTagItem,
  diffItems,
} from '@spotify/plugin-confidence-activity-react';

import { createWorkflowUpdatesChangeExtension } from './workflow-updates-change';

const tagIsHoldback = (tag: string) => {
  const [surface, , holdback] = tag.split('/');
  return (
    !!surface &&
    !!holdback &&
    holdback === 'holdbacks' &&
    surface === 'surfaces'
  );
};

const tagIsExclusivityGroup = (tag: string) => {
  const [surface, , exclusivityGroups] = tag.split('/');
  return (
    !!surface &&
    !!exclusivityGroups &&
    exclusivityGroups === 'exclusivityGroups' &&
    surface === 'surfaces'
  );
};

interface SegmentData {
  allocation?: {
    exclusiveTo?: string[];
  };
}

const getExclusiveTo = (value: unknown): string[] => {
  // The value is the segment object directly at /module_data/flags/segment
  const segment = value as SegmentData | null;
  return segment?.allocation?.exclusiveTo ?? [];
};

const categorizeTags = (tags: string[]) => {
  return {
    exclusivityGroups: tags.filter(tagIsExclusivityGroup),
    holdbacks: tags.filter(tagIsHoldback),
  };
};

const pluralizeExclusivityGroup = (count: number) =>
  count === 1 ? 'exclusivity group' : 'exclusivity groups';

const pluralizeHoldback = (count: number) =>
  count === 1 ? 'holdback' : 'holdbacks';

const SegmentChangeRender = ({ change }: ActivityChangeOptions) => {
  const oldExclusiveTo = getExclusiveTo(change.oldValue);
  const newExclusiveTo = getExclusiveTo(change.newValue);

  const { added, removed } = diffItems(
    oldExclusiveTo,
    newExclusiveTo,
    tag => tag,
  );

  const addedCategorized = categorizeTags(added);
  const removedCategorized = categorizeTags(removed);

  // Skip rendering when titleElement shows single-item details
  const totalAdded = added.length;
  const totalRemoved = removed.length;
  if (
    (totalAdded === 1 && totalRemoved === 0) ||
    (totalRemoved === 1 && totalAdded === 0)
  ) {
    return null;
  }

  return (
    <>
      {addedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralizeExclusivityGroup(addedCategorized.exclusivityGroups.length)}`}
        >
          {addedCategorized.exclusivityGroups.map((tag, index) => (
            <ActivityTagItem
              key={index}
              tagLabel={extractLastNameComponent(tag) || tag}
              newValue=""
              ariaLabel="Exclusivity group"
            />
          ))}
        </ActivityItem.Section>
      )}

      {addedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Added ${pluralizeHoldback(addedCategorized.holdbacks.length)}`}
        >
          {addedCategorized.holdbacks.map((tag, index) => (
            <ActivityTagItem
              key={index}
              tagLabel={extractLastNameComponent(tag) || tag}
              newValue=""
              ariaLabel="Holdback"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedCategorized.exclusivityGroups.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralizeExclusivityGroup(removedCategorized.exclusivityGroups.length)}`}
        >
          {removedCategorized.exclusivityGroups.map((tag, index) => (
            <ActivityTagItem
              key={index}
              tagLabel={extractLastNameComponent(tag) || tag}
              oldValue=""
              ariaLabel="Exclusivity group"
            />
          ))}
        </ActivityItem.Section>
      )}

      {removedCategorized.holdbacks.length > 0 && (
        <ActivityItem.Section
          label={`Removed ${pluralizeHoldback(removedCategorized.holdbacks.length)}`}
        >
          {removedCategorized.holdbacks.map((tag, index) => (
            <ActivityTagItem
              key={index}
              tagLabel={extractLastNameComponent(tag) || tag}
              oldValue=""
              ariaLabel="Holdback"
            />
          ))}
        </ActivityItem.Section>
      )}
    </>
  );
};

export const SEGMENT_CHANGE_EXTENSION = createWorkflowUpdatesChangeExtension({
  id: 'segment-change',
  priority: 10,
  predicate: ({ change }) => {
    if (change.fieldPath !== '/module_data/flags/segment') {
      return false;
    }

    const oldExclusiveTo = getExclusiveTo(change.oldValue);
    const newExclusiveTo = getExclusiveTo(change.newValue);

    const { added, removed } = diffItems(
      oldExclusiveTo,
      newExclusiveTo,
      tag => tag,
    );

    // Only show if there are holdback or exclusivity group changes
    const addedCategorized = categorizeTags(added);
    const removedCategorized = categorizeTags(removed);

    const hasRelevantChanges =
      addedCategorized.holdbacks.length > 0 ||
      addedCategorized.exclusivityGroups.length > 0 ||
      removedCategorized.holdbacks.length > 0 ||
      removedCategorized.exclusivityGroups.length > 0;

    return hasRelevantChanges;
  },
  title: change => {
    const oldExclusiveTo = getExclusiveTo(change.oldValue);
    const newExclusiveTo = getExclusiveTo(change.newValue);

    const { added, removed } = diffItems(
      oldExclusiveTo,
      newExclusiveTo,
      tag => tag,
    );

    const addedCategorized = categorizeTags(added);
    const removedCategorized = categorizeTags(removed);

    // Single item changes
    if (added.length === 1 && removed.length === 0) {
      if (addedCategorized.exclusivityGroups.length === 1) {
        return 'Added exclusivity group';
      }
      if (addedCategorized.holdbacks.length === 1) {
        return 'Added holdback';
      }
    }

    if (removed.length === 1 && added.length === 0) {
      if (removedCategorized.exclusivityGroups.length === 1) {
        return 'Exclusivity group removed';
      }
      if (removedCategorized.holdbacks.length === 1) {
        return 'Holdback removed';
      }
    }

    return 'Updated surfaces';
  },
  titleElement: change => {
    const oldExclusiveTo = getExclusiveTo(change.oldValue);
    const newExclusiveTo = getExclusiveTo(change.newValue);

    const { added, removed } = diffItems(
      oldExclusiveTo,
      newExclusiveTo,
      tag => tag,
    );

    const addedCategorized = categorizeTags(added);
    const removedCategorized = categorizeTags(removed);

    if (
      added.length === 1 &&
      removed.length === 0 &&
      addedCategorized.exclusivityGroups.length === 1
    ) {
      return (
        <ActivityLabelItem
          text="Added exclusivity group"
          tagLabel={
            extractLastNameComponent(addedCategorized.exclusivityGroups[0]) ||
            addedCategorized.exclusivityGroups[0]
          }
          ariaLabel="Exclusivity group"
        />
      );
    }

    if (
      added.length === 1 &&
      removed.length === 0 &&
      addedCategorized.holdbacks.length === 1
    ) {
      return (
        <ActivityLabelItem
          text="Added holdback"
          tagLabel={
            extractLastNameComponent(addedCategorized.holdbacks[0]) ||
            addedCategorized.holdbacks[0]
          }
          ariaLabel="Holdback"
        />
      );
    }

    if (
      removed.length === 1 &&
      added.length === 0 &&
      removedCategorized.exclusivityGroups.length === 1
    ) {
      return (
        <ActivityLabelItem
          text="Removed exclusivity group"
          tagLabel={
            extractLastNameComponent(removedCategorized.exclusivityGroups[0]) ||
            removedCategorized.exclusivityGroups[0]
          }
          ariaLabel="Exclusivity group"
        />
      );
    }

    if (
      removed.length === 1 &&
      added.length === 0 &&
      removedCategorized.holdbacks.length === 1
    ) {
      return (
        <ActivityLabelItem
          text="Removed holdback"
          tagLabel={
            extractLastNameComponent(removedCategorized.holdbacks[0]) ||
            removedCategorized.holdbacks[0]
          }
          ariaLabel="Holdback"
        />
      );
    }

    return null;
  },
  render: SegmentChangeRender,
});
